/**
 * 🏭 RESOURCE FACTORY
 *
 * Why: Eliminate boilerplate for CRUD operations
 * What: Generates complete API + React Query hooks for any entity
 * Benefit: Create full CRUD in one line of code
 *
 * Example usage:
 *  const userResource = createResource<User>({endpoint:'/users',queryKey:'usesrs'});
 *
 * Now you have
 * - userResource.api.getList()
 * - userResource.api.getOne(id)
 * - userResource.api.create(data)
 * - userResource.api.update(id,data)
 * - userResource.api.delete(id)
 *
 * - userResource.hooks.useList()
 * - userResource.hooks.useOne()
 * - userResource.hooks.useCreate()
 * - userResource.hooks.useUpdate()
 * - userResource.hooks.useDelete()
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { httpService } from "../services";

import type {
  ResourceConfig,
  ResourceApi,
  ResourceHooks,
  ResourceQueryKeys,
  Resource,
  UseListOptions,
  UseOneOptions,
  UseMutationOptions,
} from "./types";

import type {
  PaginatedResponse,
  ID,
  ListParams,
  BaseEntity,
} from "../../../types";
import { logger } from "../../utils";

//==============================================
// Default Configuration
//==============================================

const DEFAULT_STALE_TIME: number = 5 * 60 * 1000; // 5 minutes

/**
 * Creates a complete resource with API methods and React Query Hooks
 *
 * @param config - Resource configuration
 * @returns Resource object with api and hooks
 */

// =======================
// 🔹 Query Key Factory
// =======================

/**
 * Generate consistent query keys for cache managment
 * Why: React Query uses keys to cache and invalidated data
 */

function createQueryKeys(queryKey: string): ResourceQueryKeys {
  return {
    // Base key for all queries of this resource
    all: [queryKey] as const,
    // All list queries
    lists: () => [queryKey, "list"] as const,
    //Specific list with params (for cache differentiation)
    list: (params?: ListParams) => [queryKey, "list", params] as const,
    // All Detail Queries
    details: () => [queryKey, "detail"] as const,
    // Specific detail query by ID
    detail: (id: ID) => [queryKey, "detail", id] as const,
    statistics: () => [queryKey, "statistics"] as const,
  };
}

// ============================
// 🔹 API Factory
// ============================

/**
 * Creates API methods for a resource
 */
function createApi<TEntity extends BaseEntity, TCreateDto, TUpdateDto>(
  endPoint: string,
  queryKey: string,
): ResourceApi<TEntity, TCreateDto, TUpdateDto> {
  return {
    /**
     * Fetches paginated list of entities
     */
    async getList(params?: ListParams): Promise<PaginatedResponse<TEntity>> {
      logger.debug(`[${queryKey}] Fetching list`, params);

      // Transform params for API
      const apiParams = transformListParams(params);
      return httpService.get<PaginatedResponse<TEntity>>(endPoint, {
        params: apiParams,
      });
    },
    /**
     * Fetch statistics
     */
    async getStatistics(): Promise<TEntity> {
      logger.debug(`[${queryKey}] Fetching stats`);
      return httpService.get<TEntity>(`${endPoint}/stats`);
    },
    /**
     * Fetches single entity by ID
     */
    async getOne(id: ID): Promise<TEntity> {
      logger.debug(`[${queryKey}] Fetching one`, id);
      return httpService.get<TEntity>(`${endPoint}/${id}`);
    },

    /**
     * Create new entity
     */
    async create(data: TCreateDto): Promise<TEntity> {
      logger.debug(`[${queryKey}] Creating:`, data);
      return httpService.post<TEntity>(endPoint, data);
    },

    /**
     * Update existing entity
     */
    async update(id: ID, data: TUpdateDto): Promise<TEntity> {
      logger.debug(`[${queryKey}] Updating:`, id, data);
      return httpService.patch<TEntity>(`${endPoint}/${id}`, data);
    },
    /**
     * Delete entity
     */
    async delete(id: ID): Promise<void> {
      logger.debug(`[${queryKey}] Deleting:`, id);
      return httpService.delete<void>(`${endPoint}/${id}`);
    },
  };
}

// =======================
// 🔹 React Query Hooks
// =======================

/**
 * React hooks for data fetching and mutations
 * Why: Integrate API with React Query for caching,loading states, etc
 */

function createHooks<TEntity extends BaseEntity, TCreateDto, TUpdateDto>(
  api: ResourceApi<TEntity, TCreateDto, TUpdateDto>,
  queryKeys: ResourceQueryKeys,
  queryKey: string,
  staleTime: number,
): ResourceHooks<TEntity, TCreateDto, TUpdateDto> {
  return {
    /**
     * Hook for fetching paginated list
     *
     * Features:
     * - Automatic caching with query params as cache key
     * - Configurable stale time
     * - Loading and error states
     *
     * @example
     * ```typescript
     * const { data, isLoading, error } = usersResource.hooks.useList({
     *   page: 1,
     *   limit: 10,
     *   sortBy: 'createdAt',
     *   sortOrder: 'desc',
     * });
     * ```
     */

    useList: (params?: ListParams, options?: UseListOptions<TEntity>) => {
      return useQuery({
        queryKey: queryKeys.list(params),
        queryFn: () => api.getList(params),
        placeholderData: keepPreviousData,
        // Keep data fresh for 5 minutes
        staleTime: options?.staleTime ?? staleTime,
        enabled: options?.enabled,
        refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
      });
    },
    /**
     * Hook for fetching stats
     */
    useStatistics: (options?: UseOneOptions<TEntity>) => {
      return useQuery({
        queryKey: queryKeys.statistics(),
        queryFn: () => api.getStatistics(),
        // Only fetch if ID is valid
        staleTime: options?.staleTime ?? staleTime,
      });
    },
    /**
     * Hook for fetching single entity
     */
    useOne: (id: ID, options?: UseOneOptions<TEntity>) => {
      return useQuery({
        queryKey: queryKeys.detail(id),
        queryFn: () => api.getOne(id),
        // Only fetch if ID is valid
        staleTime: options?.staleTime ?? staleTime,
        enabled: (options?.enabled ?? true) && !!id,
      });
    },
    /**
     * Hook for creating entity
     * Features:
     * - Optimistic updates
     * - Automatic cache invalidation
     * - Success/error callbacks
     */
    useCreate: (options?: UseMutationOptions<TEntity, TCreateDto>) => {
      const queryClient = useQueryClient();

      return useMutation({
        mutationFn: (data: TCreateDto) => api.create(data),
        // On success, invalidates list queries to refetch
        onSuccess: (data, variables) => {
          queryClient.invalidateQueries({ queryKey: queryKeys.lists() });
          //Call custom Success handler
          options?.onSuccess?.(data, variables);
          logger.info(`[${queryKey}] Create successsful,cache invalidated`);
        },
        onError: (error, variables) => {
          options?.onError?.(error, variables);
          logger.error(`[${queryKey}] Create failed`, error);
        },
        onSettled: options?.onSettled,
      });
    },

    /**
     * Hooks for updating entity
     */
    useUpdate: (
      options?: UseMutationOptions<TEntity, { id: ID; data: TUpdateDto }>,
    ) => {
      const queryClient = useQueryClient();

      return useMutation({
        mutationFn: ({ id, data }: { id: ID; data: TUpdateDto }) =>
          api.update(id, data),
        // Optimistic update: Update cache immediatly  before Api Call
        onMutate: async ({ id, data }) => {
          // Cancel outgoing queries
          await queryClient.cancelQueries({ queryKey: queryKeys.detail(id) });

          // Snapshot current values
          const previousData = queryClient.getQueryData<TEntity>(
            queryKeys.detail(id),
          );

          // Optimistically update cache
          if (previousData) {
            queryClient.setQueryData<TEntity>(queryKeys.detail(id), {
              ...previousData,
              ...data,
              updatedAt: new Date().toISOString(),
            });
          }
          //Return context with snapshot for rollback
          return { previousData };
        },
        // On error, rollback to snapshot
        onError: (error, { id }, context) => {
          if (context?.previousData) {
            queryClient.setQueryData(
              queryKeys.detail(id),
              context.previousData,
            );
          }
          options?.onError?.(error, { id, data: {} as TUpdateDto });
          logger.error(`[${queryKey}] Update failed:`, error);
        },
        // On success or error,refetch to ensure consistency
        onSettled: (data, error, { id }) => {
          queryClient.invalidateQueries({ queryKey: queryKeys.detail(id) });
          queryClient.invalidateQueries({ queryKey: queryKeys.lists() });
          options?.onSettled?.(data, error, { id, data: {} as TUpdateDto });
        },
      });
    },
    /**
     * Hooks for deleting entity
     */
    useDelete: (options?: UseMutationOptions<void, ID>) => {
      const queryClient = useQueryClient();
      return useMutation({
        mutationFn: (id: ID) => api.delete(id),
        // Optimistic update: Remove from cache immediatly
        onMutate: async (id) => {
          await queryClient.cancelQueries({ queryKey: queryKeys.lists() });
          // Snapshot all list queries for rollback
          const previousLists = queryClient.getQueriesData<
            PaginatedResponse<TEntity>
          >({
            queryKey: queryKeys.lists(),
          });
          // Remove item from all list queries
          previousLists.forEach(([key, data]) => {
            if (data) {
              console.log("data in useDelete", data);
              queryClient.setQueryData<PaginatedResponse<TEntity>>(key, {
                ...data,
                data: data.data.filter((item) => item._id !== id),
                meta: {
                  ...data.meta,
                  totalItems: data.meta.totalItems - 1,
                },
              });
            }
          });
          return { previousLists };
        },
        // On error, rollback
        onError: (error, id, context) => {
          if (context?.previousLists) {
            context.previousLists.forEach(([key, data]) => {
              queryClient.setQueryData(key, data);
            });
          }
          options?.onError?.(error, id);
          logger.error(`[${queryKey}] Delete failed:`, error);
        },
        // on success invalidate cache
        onSuccess: (data, id) => {
          queryClient.invalidateQueries({ queryKey: queryKeys.lists() });
          queryClient.removeQueries({ queryKey: queryKeys.detail(id) });
          options?.onSuccess?.(data, id);
          logger.info(`[${queryKey}] Delete Successful`);
        },
      });
    },
    queryKeys,
  };
}
//======================================
//  Helper Functions
//======================================

function transformListParams(params?: ListParams): Record<string, unknown> {
  if (!params) return {};
  const { filters, ...rest } = params;

  //Convert filters array to query string format
  const filterParams: Record<string, unknown> = {};
  if (filters && filters.length > 0) {
    filters.forEach((filter, index) => {
      filterParams[`filters[${index}][field]`] = filter.field;
      filterParams[`filters[${index}][operator]`] = filter.operator;
      filterParams[`filters[${index}][value]`] = filter.value;
    });
  }

  return {
    ...rest,
    ...filterParams,
  };
}

// ============================
// 🔹 Main Factory Function
// ============================

/**
 * Creates a complete resource with API methods and React Query hooks
 *
 * @param config - Resource configuration
 * @returns Resource object with api, hooks, and queryKeys
 *
 * @example
 * ```typescript
 * // Define your entity types
 * interface User extends BaseEntity {
 *   name: string;
 *   email: string;
 *   role: 'admin' | 'user';
 * }
 *
 * interface CreateUserDto {
 *   name: string;
 *   email: string;
 *   password: string;
 * }
 *
 * interface UpdateUserDto {
 *   name?: string;
 *   email?: string;
 * }
 *
 * // Create the resource
 * export const usersResource = createResource<User, CreateUserDto, UpdateUserDto>({
 *   endpoint: '/api/users',
 *   queryKey: 'users',
 *   staleTime: 10 * 60 * 1000, // 10 minutes
 * });
 *
 * // Use in components
 * const { data, isLoading } = usersResource.hooks.useList({ page: 1 });
 * const createUser = usersResource.hooks.useCreate();
 * ```
 */

export function createResource<
  TEntity extends BaseEntity,
  TCreateDto = Partial<TEntity>,
  TUpdateDto = Partial<TEntity>,
>(config: ResourceConfig): Resource<TEntity, TCreateDto, TUpdateDto> {
  const { endpoint, queryKey, staleTime = DEFAULT_STALE_TIME } = config;

  // Create query keys factory
  const queryKeys = createQueryKeys(queryKey);

  // Create API methods
  const api = createApi<TEntity, TCreateDto, TUpdateDto>(endpoint, queryKey);

  // Create React Query Hooks
  const hooks = createHooks<TEntity, TCreateDto, TUpdateDto>(
    api,
    queryKeys,
    queryKey,
    staleTime,
  );

  logger.info(`✅ Resource created: ${queryKey} (${endpoint})`);

  return {
    api,
    hooks,
    queryKeys,
  };
}

// ============================
// 🔹 Extended Resource Factory
// ============================

/**
 * Creates a resource with additional custom operations
 *
 * @param config - Resource configuration
 * @param extendApi - Function to add custom API methods
 * @param extendHooks - Function to add custom hooks
 *
 * @example
 * ```typescript
 * export const usersResource = createExtendedResource<User>(
 *   { endpoint: '/api/users', queryKey: 'users' },
 *   (api) => ({
 *     ...api,
 *     // Custom API methods
 *     async activateUser(id: ID): Promise<User> {
 *       return httpService.post(`/api/users/${id}/activate`);
 *     },
 *     async deactivateUser(id: ID): Promise<User> {
 *       return httpService.post(`/api/users/${id}/deactivate`);
 *     },
 *   }),
 *   (hooks, queryKeys) => ({
 *     ...hooks,
 *     // Custom hooks
 *     useActivate: () => {
 *       const queryClient = useQueryClient();
 *       return useMutation({
 *         mutationFn: (id: ID) => api.activateUser(id),
 *         onSuccess: () => {
 *           queryClient.invalidateQueries({ queryKey: queryKeys.lists() });
 *         },
 *       });
 *     },
 *   })
 * );
 * ```
 */

export function createExtendedResource<
  TEntity extends BaseEntity,
  TCreateDto = Partial<TEntity>,
  TUpdateDto = Partial<TEntity>,
  TExtendedApi extends ResourceApi<TEntity, TCreateDto, TUpdateDto> =
    ResourceApi<TEntity, TCreateDto, TUpdateDto>,
  TExtendedHooks extends ResourceHooks<TEntity, TCreateDto, TUpdateDto> =
    ResourceHooks<TEntity, TCreateDto, TUpdateDto>,
>(
  config: ResourceConfig,
  extendApi?: (
    api: ResourceApi<TEntity, TCreateDto, TUpdateDto>,
  ) => TExtendedApi,
  extendHooks?: (
    hooks: ResourceHooks<TEntity, TCreateDto, TUpdateDto>,
    queryKeys: ResourceQueryKeys,
    api: TExtendedApi, // 👈 Add here in the TYPE definition
  ) => TExtendedHooks,
): {
  api: TExtendedApi;
  hooks: TExtendedHooks;
  queryKeys: ResourceQueryKeys;
} {
  const baseResource = createResource<TEntity, TCreateDto, TUpdateDto>(config);

  const api = extendApi
    ? extendApi(baseResource.api)
    : (baseResource.api as TExtendedApi);

  const hooks = extendHooks
    ? extendHooks(baseResource.hooks, baseResource.queryKeys, api) // 👈 Pass api here
    : (baseResource.hooks as TExtendedHooks);

  return {
    api,
    hooks,
    queryKeys: baseResource.queryKeys,
  };
}
